import React from 'react';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "../components/StripeCheckoutForm"; // Ensure this path is correct
import { Navigate, useLocation } from "react-router-dom";

// Make sure your Stripe publishable key is correctly set in your .env file
const stripePublishableKey = process.env.REACT_APP_STRIPE_PK;

if (!stripePublishableKey) {
  console.error("Stripe Publishable Key is not set. Please check your .env file for REACT_APP_STRIPE_PK.");
  // alert("Payment system is currently unavailable. Stripe key is missing."); // Avoid alert
}
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// SVG Icon for Secure Payment (Shield with a checkmark)
const SecurePaymentIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="28" 
    height="28" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    style={{ marginRight: '10px', color: '#28a745' }} // Green color for security
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <path d="m9 12 2 2 4-4"></path>
  </svg>
);


const Purchase = () => {
  const location = useLocation();
  const fullFormData = location.state?.fullFormData;

  if (!fullFormData || !fullFormData.items || !fullFormData.userInfo) {
    console.error("Purchase page: fullFormData or its parts (items, userInfo) are missing from location state.");
    // Consider showing a user-friendly message or redirecting with a toast
    return <Navigate to="/checkout" replace />;
  }

  const items = fullFormData.items;
  const userInfo = fullFormData.userInfo;

  if (!items || items.length === 0 || !userInfo || Object.keys(userInfo).length === 0) {
    console.error("Purchase page: Items array is empty or userInfo object is empty.");
    return <Navigate to="/checkout" replace />;
  }

  // Extract values for summary display
  const itemsSubtotal = parseFloat(userInfo.itemsSubtotal || 0);
  const couponCode = userInfo.couponCodeApplied;
  const couponDiscount = parseFloat(userInfo.couponDiscountAmount || 0);
  // subtotalAfterCoupon is itemsSubtotal - couponDiscount. If not present, calculate.
  const subtotalAfterCoupon = userInfo.subtotalAfterCoupon !== undefined 
                              ? parseFloat(userInfo.subtotalAfterCoupon) 
                              : itemsSubtotal - couponDiscount;
  const shippingAmount = parseFloat(userInfo.shippingAmount || 0);
  const payableAmount = parseFloat(userInfo.payableAmount || 0);


  const pageStyle = {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5", 
    paddingTop: "60px", 
    paddingBottom: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const cardStyle = {
    backgroundColor: "#ffffff", 
    borderRadius: "12px", 
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)", 
    overflow: "hidden", 
    borderTop: "5px solid transparent",
    borderImageSource: "linear-gradient(to right, #e5272e, #f59332)",
    borderImageSlice: 1,
    borderImageWidth: "5px 0 0 0",
    borderStyle: "solid",
  };
  
  const headerContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center', 
    marginBottom: '1rem', // Reduced margin slightly
  }

  const headerTextStyle = {
    color: "#1c2938", 
    fontWeight: "600", 
    fontSize: "1.75rem", 
  };
  
  const summaryItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  };

  const summaryLabelStyle = {
    color: '#555',
  };

  const summaryValueStyle = {
    fontWeight: '500',
    color: '#333',
  };
  
  const totalAmountDisplayForHeaderStyle = { // Style for the total amount in the header paragraph
    color: "#e5272e", 
    fontWeight: "bold",
    fontSize: "1.1rem", // Kept from original
  };

  const poweredByStyle = {
    color: "#555", 
    fontSize: "0.85rem",
  }

  return (
    <div style={pageStyle} className="purchase-page-wrapper">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-7"> {/* Adjusted column width for content */}
            <div className="card" style={cardStyle}>
              <div className="card-body p-4 p-md-5">
                <div style={headerContainerStyle}>
                  <SecurePaymentIcon />
                  <h2 className="card-title text-center mb-0" style={headerTextStyle}> 
                    Secure Payment
                  </h2>
                </div>
                
                <p className="text-center text-muted mb-3" style={{fontSize: '0.95rem'}}>
                  Review your order and complete your purchase.
                </p>

                {/* Order Summary Section */}
                <div className="mb-4 p-3 bg-light rounded-3" style={{border: '1px solid #e9ecef'}}>
                  <h5 className="mb-3 fw-medium" style={{color: "#0f4372"}}>Order Confirmation</h5>
                  
                  <div style={summaryItemStyle}>
                    <span style={summaryLabelStyle}>Subtotal (items):</span>
                    <span 
                        style={{
                            ...summaryValueStyle, 
                            textDecoration: couponCode ? 'line-through' : 'none',
                            color: couponCode ? '#6c757d' : summaryValueStyle.color
                        }}
                    >
                        £{itemsSubtotal.toFixed(2)}
                    </span>
                  </div>

                  {couponCode && (
                    <div style={{...summaryItemStyle, color: '#28a745'}}> {/* Green for discount */}
                      <span style={summaryLabelStyle}>Coupon ({couponCode}):</span>
                      <span style={summaryValueStyle}>- £{couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Optional: Display subtotal after coupon if you want an explicit line for it */}
                  {/* {couponCode && (
                    <div style={summaryItemStyle}>
                        <span style={summaryLabelStyle}>Subtotal after coupon:</span>
                        <span style={summaryValueStyle}>£{subtotalAfterCoupon.toFixed(2)}</span>
                    </div>
                  )} */}

                  <div style={summaryItemStyle}>
                    <span style={summaryLabelStyle}>Shipping:</span>
                    <span style={summaryValueStyle}>£{shippingAmount.toFixed(2)}</span>
                  </div>
                  <hr className="my-2" />
                  <div style={{...summaryItemStyle, fontSize: '1.1rem', marginTop: '0.75rem'}}>
                    <strong style={{color: '#1c2938'}}>Grand Total:</strong>
                    <strong style={{color: '#e5272e'}}>£{payableAmount.toFixed(2)}</strong>
                  </div>
                </div>
                
                {/* Stripe Elements Form */}
                {stripePromise && stripePublishableKey ? (
                  <Elements stripe={stripePromise}>
                    {/* Pass items and userInfo which includes the final payableAmount */}
                    <CheckoutForm items={items} userInfo={userInfo} />
                  </Elements>
                ) : (
                  <div className="alert alert-warning text-center" role="alert">
                    <h4 className="alert-heading" style={{color: "#856404"}}>Payment System Issue</h4>
                    <p style={{color: "#856404"}}>We're currently facing a temporary issue with our payment gateway. Please try again shortly.</p>
                    <hr />
                    <p className="mb-0" style={{color: "#856404"}}>If the problem persists, please contact support.</p>
                  </div>
                )}
              </div>
            </div>
            <p className="text-center mt-4" style={poweredByStyle}>
                Securely Powered by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Purchase;
